import React, {useState} from 'react';
import {Button, Text, Icon} from '@gravity-ui/uikit';
import {Plus, FloppyDisk, ArrowRotateLeft, Arrows3RotateLeft} from '@gravity-ui/icons';
import {DndContext} from '@dnd-kit/core';
import {SortableContext, rectSortingStrategy} from '@dnd-kit/sortable';
import {ActionModal, useNotifications} from '../../shared';
import {
  useStoryScreensStore,
  useStoryScreensDragAndDrop,
  useSaveStoryScreens,
  StoryScreenCard,
  AddStoryScreenModal,
} from '../../features';
import styles from './UniversalStoryScreensWidget.module.scss';

// Универсальный интерфейс для API операций со сторис
interface StoryApiOperations {
  saveScreens: (storyId: string, data: any) => Promise<void>;
  queryKeyPattern: string[]; // паттерн для invalidateQueries
}

// Интерфейс для данных истории
interface StoryData {
  publishedAt: string | null;
}

interface UniversalStoryScreensWidgetProps {
  storyId: string;
  storyData: StoryData | undefined;
  apiOperations: StoryApiOperations;
}

export const UniversalStoryScreensWidget: React.FC<UniversalStoryScreensWidgetProps> = ({
  storyId,
  storyData,
  apiOperations,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isPublishConfirmModalOpen, setIsPublishConfirmModalOpen] = useState(false);
  const {getScreens, resetScreens, hasChanges} = useStoryScreensStore();
  const {createNotification} = useNotifications();

  // Получаем экраны для конкретной истории
  const screens = getScreens(storyId);

  // Инициализируем drag-and-drop
  const dragAndDrop = useStoryScreensDragAndDrop({
    storyId,
    screens,
  });

  // Сохранение вместе с получением сохранённых данных обратно — одна фаза,
  // на время которой правки экранов запрещены
  const {saveScreens, retrySync, isSyncFailed, isSaving} = useSaveStoryScreens({
    storyId,
    screens,
    apiOperations,
  });

  const handleSaveScreens = () => {
    if (storyData?.publishedAt) {
      setIsPublishConfirmModalOpen(true);
    } else {
      performSaveScreens();
    }
  };

  const performSaveScreens = () => {
    saveScreens();
    setIsPublishConfirmModalOpen(false);
  };

  const confirmReset = () => {
    resetScreens(storyId);
    setIsResetModalOpen(false);
    createNotification({
      title: 'Изменения сброшены',
      name: 'resetScreens',
      theme: 'info',
    });
  };

  const isDirty = hasChanges(storyId);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text variant="subheader-3">Экраны истории</Text>
        <div className={styles.actions}>
          {isSyncFailed ? (
            // Сохранение прошло, но обновлённые экраны с сервера не получены.
            // Пока это не исправлено, редактирование заблокировано: у сохранённых
            // медиа нет постоянных имён файлов, и следующее сохранение их потеряет.
            <Button view="action" size="m" onClick={retrySync}>
              <Icon data={Arrows3RotateLeft} />
              Повторить обновление
            </Button>
          ) : (
            <>
              <Button
                view="outlined"
                size="m"
                onClick={() => setIsResetModalOpen(true)}
                disabled={screens.length === 0 || isSaving}>
                <Icon data={ArrowRotateLeft} />
                Сбросить
              </Button>
              <Button
                view="action"
                size="m"
                onClick={handleSaveScreens}
                loading={isSaving}
                disabled={!isDirty || screens.length === 0 || isSaving}>
                <Icon data={FloppyDisk} />
                Сохранить
              </Button>
            </>
          )}
        </div>
      </div>

      {isSyncFailed && (
        <Text variant="body-2" color="warning">
          Экраны сохранены, но получить их с сервера не удалось. Повторите обновление или перезагрузите страницу —
          до этого редактирование недоступно.
        </Text>
      )}

      <div className={styles.screensGrid}>
        <DndContext
          sensors={dragAndDrop.sensors}
          collisionDetection={dragAndDrop.collisionDetection}
          onDragEnd={dragAndDrop.handleDragEnd}>
          <SortableContext
            items={dragAndDrop.currentScreens.map((screen) => screen.id!)}
            strategy={rectSortingStrategy}>
            {dragAndDrop.currentScreens.map((screen) => (
              <StoryScreenCard
                key={screen.id}
                screen={screen}
                storyId={storyId}
                isDragDisabled={dragAndDrop.isDragDisabled || isSaving}
                isEditDisabled={isSaving}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div
          className={styles.defaultScreen}
          onClick={() => {
            if (!isSaving) {
              setIsAddModalOpen(true);
            }
          }}>
          <Button view="outlined" size="xl" disabled={isSaving}>
            <Icon data={Plus} />
          </Button>
        </div>
      </div>

      <AddStoryScreenModal open={isAddModalOpen} setOpen={setIsAddModalOpen} storyId={storyId} />

      <ActionModal
        open={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        setOpen={setIsResetModalOpen}
        caption="Сбросить изменения"
        textButtonApply="Сбросить"
        textButtonCancel="Отмена"
        size="s"
        buttonsSize="l"
        viewActionButton="outlined-danger"
        submitForm={confirmReset}>
        <Text variant="body-2" color="secondary">
          Вы уверены, что хотите сбросить все изменения в экранах истории?
        </Text>
      </ActionModal>

      {/* Модалка подтверждения для опубликованных историй */}
      <ActionModal
        open={isPublishConfirmModalOpen}
        onClose={() => setIsPublishConfirmModalOpen(false)}
        setOpen={setIsPublishConfirmModalOpen}
        caption="Подтверждение сохранения"
        textButtonApply="Сохранить"
        textButtonCancel="Отмена"
        size="s"
        buttonsSize="l"
        submitForm={performSaveScreens}>
        <Text variant="body-2" color="secondary">
          История уже опубликована. Изменения в экранах будут видны пользователям сразу после сохранения. Продолжить?
        </Text>
      </ActionModal>
    </div>
  );
};
